/**
 * Fetch-based SSE client for the gate-events diagnostic proxy
 * (`admin/super-admin/devices/events-stream`). Deliberately separate from
 * `RealtimeEventsService` — that service's token resolution only supports
 * 'user'/'tunnel' modes and is used by real-time paths elsewhere in the app;
 * this stays scoped to the unlisted gate-events page.
 *
 * Native EventSource can't carry the super-admin bearer token, so this mirrors
 * RealtimeEventsService's approach: fetch() + manual `data:`/`event:` frame
 * parsing over the response's ReadableStream.
 */

export interface GateStreamFrame {
  cmd: string;
  arg?: string;
  name?: string;
  /** Local receive time — the basis for the Option 1 time-gap heuristic. */
  receivedAt: number;
}

export type GateStreamStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface GateStreamHandle {
  disconnect: () => void;
}

/** Matches sse-api's `defaultEventName: 'gate_command'` (createKeyedConnectionRegistry). */
const GATE_COMMAND_EVENT = 'gate_command';

export function connectGateEventStream(
  serial: string,
  baseUrl: string,
  token: string,
  onEvent: (frame: GateStreamFrame) => void,
  onStatusChange?: (status: GateStreamStatus) => void
): GateStreamHandle {
  const controller = new AbortController();
  void runStream();

  async function runStream(): Promise<void> {
    onStatusChange?.('connecting');
    try {
      const url = `${baseUrl}/admin/super-admin/devices/events-stream?serial=${encodeURIComponent(serial)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        cache: 'no-store',
      });

      if (!response.ok || !response.body) {
        throw new Error(`Gate event stream HTTP ${response.status}`);
      }

      onStatusChange?.('open');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split(/\r?\n\r?\n/);
        buffer = chunks.pop() ?? '';
        for (const chunk of chunks) {
          dispatchParsedBlock(chunk, onEvent);
        }
      }

      if (!controller.signal.aborted) {
        onStatusChange?.('closed');
      }
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }
      onStatusChange?.('error');
    }
  }

  return {
    disconnect: () => controller.abort(),
  };
}

function dispatchParsedBlock(block: string, onEvent: (frame: GateStreamFrame) => void): void {
  const trimmed = block.trim();
  // Keepalive comments (`: keepalive`) and blank frames carry no gate command.
  if (!trimmed || trimmed.startsWith(':')) {
    return;
  }

  let type = 'message';
  const dataLines: string[] = [];

  for (const line of trimmed.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      type = line.slice(6).trim() || type;
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  if (type !== GATE_COMMAND_EVENT) {
    return;
  }

  const dataRaw = dataLines.join('\n');
  if (!dataRaw) {
    return;
  }

  let data: any;
  try {
    data = JSON.parse(dataRaw);
  } catch {
    return;
  }

  onEvent({
    cmd: data?.cmd,
    arg: data?.arg,
    name: data?.name,
    receivedAt: Date.now(),
  });
}
